import { IDot } from 'models/ICattlesData'
import moment from 'moment'

import { MAX_MONTHLY_CHART_POINTS } from './chartDateAggregation'

export const buildInterpolatedSeries = (
	categories: (number | null)[],
): (number | null)[] => {
	const result: (number | null)[] = new Array(categories.length).fill(null)
	for (let i = 0; i < categories.length; i++) {
		if (categories[i] !== null) continue

		let start = i
		while (start > 0 && categories[start - 1] === null) start--
		let end = i
		while (end < categories.length - 1 && categories[end + 1] === null) end++

		const prev = categories[start - 1]
		const next = categories[end + 1]

		if (typeof prev === 'number' && typeof next === 'number') {
			const totalSteps = end - start + 2
			result[start - 1] = prev
			for (let j = start; j <= end; j++)
				result[j] = prev + ((next - prev) / totalSteps) * (j - start + 1)
			result[end + 1] = next
		} else if (typeof prev === 'number') {
			result[start - 1] = prev
			for (let j = start; j <= end; j++) result[j] = prev
		} else if (typeof next === 'number') {
			for (let j = start; j <= end; j++) result[j] = next
			result[end + 1] = next
		}
		i = end + 1
	}
	return result
}

export const buildChartAxisTimestamps = (
	startDate: number,
	endDate: number,
	monthly: boolean,
): number[] => {
	const timestamps: number[] = []

	if (monthly) {
		let current = moment(startDate).startOf('month')
		const end = moment(endDate).startOf('month')
		while (current.isSameOrBefore(end)) {
			timestamps.push(current.valueOf())
			current = current.clone().add(1, 'month')
		}
		return timestamps.slice(-MAX_MONTHLY_CHART_POINTS)
	}

	let current = moment(startDate).startOf('day')
	const end = moment(endDate).startOf('day')
	while (current.isSameOrBefore(end)) {
		timestamps.push(current.valueOf())
		current = current.clone().add(1, 'day')
	}
	return timestamps
}

export const alignDotsToTimestamps = (
	dots: IDot[],
	timestamps: number[],
	monthly: boolean,
): (number | null)[] => {
	const valueByTs = new Map<number, number>()

	dots.forEach(dot => {
		if (dot.value == null || !Number.isFinite(dot.value)) return
		const date = moment(dot.date_ms ?? dot.date)
		if (!date.isValid()) return
		const ts = monthly
			? date.clone().startOf('month').valueOf()
			: date.clone().startOf('day').valueOf()
		valueByTs.set(ts, dot.value)
	})

	return timestamps.map(ts => (valueByTs.has(ts) ? valueByTs.get(ts)! : null))
}

export const hasChartSeriesData = (categories: (number | null)[][]): boolean =>
	categories.some(series => series.some(value => value != null))

export const PLACEHOLDER_WAVE_POINT_COUNT = 121

export const buildPlaceholderWave = (length: number): number[] => {
	if (length <= 0) return []
	if (length === 1) return [0.5]

	return Array.from({ length }, (_, index) => {
		const progress = index / (length - 1)
		return 0.5 - 0.25 * Math.sin(progress * Math.PI * 3)
	})
}

export const buildPlaceholderAxis = (
	timestamps: number[],
): { timestamps: number[]; values: number[] } => {
	if (timestamps.length === 0) {
		return { timestamps: [], values: [] }
	}

	const start = timestamps[0]
	const last = timestamps[timestamps.length - 1]
	const end = last > start ? last : moment(start).endOf('day').valueOf()
	const denseTimestamps = Array.from(
		{ length: PLACEHOLDER_WAVE_POINT_COUNT },
		(_, index) =>
			start + ((end - start) * index) / (PLACEHOLDER_WAVE_POINT_COUNT - 1),
	)

	return {
		timestamps: denseTimestamps,
		values: buildPlaceholderWave(denseTimestamps.length),
	}
}
